import { Cropper } from "react-cropper";
import { useEffect, useRef, useState } from "react";
import "cropperjs/dist/cropper.css";
import axios from "axios";
import { toast } from "react-toastify";
import { Grid, Button, Typography, Input, Box } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  getFileSizeOnlyValue,
  getMultipartHeader,
} from "../../../utility/utility";
import { API_ENDPOINT } from "../../../constants/constants";

function ProductImageCrop({ onUpdated, show, setShow }) {
  const aspect_ratio = 1;
  const maxOutputWidth = 1080; // Maximum dimension to send to server
  const compressionQuality = 0.85; // 85% JPEG compression

  const cropperRef = useRef(null);
  const image_selector = useRef();
  const [uploading, setUploading] = useState(false);
  const [image, setImage] = useState(null);
  const [cropperKey, setCropperKey] = useState(Date.now());

  useEffect(() => {
    setImage(null);
    if (image_selector.current) {
      image_selector.current.value = "";
    }
    if (cropperRef.current && cropperRef.current.cropper) {
      cropperRef.current.cropper.reset();
    }
    setCropperKey(Date.now());
  }, [show]);

  const onInputChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Replaced the strict 2MB limit with a 50MB browser safety threshold
    let mbSize = getFileSizeOnlyValue(file, "MB");
    if (mbSize > 50) {
      toast.info(
        "Image is too large for the browser. Please use an image under 50MB."
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const cancelCrop = () => {
    setImage(null);
    if (image_selector.current) {
      image_selector.current.value = "";
    }
    setShow(false);
  };

  const uploadImage = async () => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;

    if (!cropper) return;
    setUploading(true);

    try {
      // 1. Prepare canvas parameters for downscaling
      const canvasOptions = {
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      };

      // 2. Prevent massive images from hitting the server by capping the width
      const originalCroppedWidth = cropper.getData().width;
      if (originalCroppedWidth > maxOutputWidth) {
        canvasOptions.width = maxOutputWidth;
        canvasOptions.height = Math.round(maxOutputWidth / aspect_ratio);
      }

      const canvas = cropper.getCroppedCanvas(canvasOptions);

      // 3. Use native .toBlob() instead of the heavy Base64 manual loop
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            setUploading(false);
            toast.error("Failed to process image canvas.");
            return;
          }

          let formData = new FormData();
          // Sending as a .jpg to benefit from the compression
          formData.append("image", blob, "product-image.jpg");

          try {
            const { data } = await axios.post(
              `${API_ENDPOINT}/user/upload-image-file`,
              formData,
              getMultipartHeader()
            );

            setUploading(false);
            toast.success("Uploaded Successfully");
            onUpdated(data.message);
            cancelCrop();
          } catch (apiError) {
            setUploading(false);
            toast.error(apiError?.response?.data?.message || apiError.message);
          }
        },
        "image/jpeg", // Convert to JPEG
        compressionQuality // Apply compression
      );
    } catch (error) {
      setUploading(false);
      toast.error(error.message || "An error occurred while cropping.");
    }
  };

  return (
    <Box style={{ height: "70vh" }}>
      <Box
        sx={{
          display: "table",
          color: "#777",
          fontSize: "14px",
          padding: "8px",
          border: "1px dotted #ddd",
          marginBottom: "16px",
        }}
      >
        <Input
          type="file"
          inputProps={{ accept: "image/jpeg, image/png, image/jpg" }}
          onChange={onInputChange}
          inputRef={image_selector} // Make sure ref is attached here
        />
        <Typography variant="body2">
          Aspect Ratio: {aspect_ratio}:1 (Auto-compressed on upload)
        </Typography>
        <Typography variant="body2">
          Supported Formats: PNG, JPEG, JPG
        </Typography>
      </Box>

      {image && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Cropper
              key={cropperKey}
              src={image}
              style={{ height: 300, width: "100%" }}
              initialAspectRatio={aspect_ratio}
              aspectRatio={aspect_ratio}
              preview=".img-preview"
              ref={cropperRef}
              viewMode={1}
              guides={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography align="center">Preview</Typography>
            <div
              key={cropperKey}
              className="img-preview"
              style={{
                width: "100%",
                height: "300px", // Matched height with cropper for better layout
                overflow: "hidden",
                display: "inline-block",
              }}
            ></div>
          </Grid>
        </Grid>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "24px",
        }}
      >
        <LoadingButton
          loading={uploading}
          variant="contained"
          color="primary"
          onClick={uploadImage}
          disabled={!image} // Don't allow clicking upload if no image is selected
          style={{ margin: "0 8px" }}
        >
          Upload
        </LoadingButton>
        <Button
          variant="outlined"
          color="secondary"
          onClick={cancelCrop}
          style={{ margin: "0 8px" }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

export default ProductImageCrop;
